import * as React from "react";

import {
  ActionService,
  ImageRegistryService,
  TriggerService,
} from "@/shared/domain/actions";
import { AdminService } from "@/shared/domain/admin";
import { ConfigService } from "@/shared/domain/config";
import { DatasetService } from "@/shared/domain/datasets";
import { DeviceService } from "@/shared/domain/devices";
import { EventService } from "@/shared/domain/events";
import { ExperienceService } from "@/shared/domain/experiences";
import { FileService } from "@/shared/domain/files";
import { HealthService } from "@/shared/domain/health";
import { IntegrationService } from "@/shared/domain/integrations";
import { LayoutService } from "@/shared/domain/layouts";
import { LimitService } from "@/shared/domain/limits/LimitService";
import { MeteringService } from "@/shared/domain/metering/MeteringService";
import { NotificationService } from "@/shared/domain/notifications";
import { OrgService } from "@/shared/domain/orgs";
import { QueryService } from "@/shared/domain/query";
import { SecretService } from "@/shared/domain/secrets/SecretService";
import { TokenService } from "@/shared/domain/tokens";
import { TopicService } from "@/shared/domain/topics";
import { UserService } from "@/shared/domain/users/UserService";
import { WorkspaceService } from "@/shared/domain/workspaces/WorkspaceService";

interface Context {
  actionService: ActionService;
  adminService: AdminService;
  configService: ConfigService;
  datasetService: DatasetService;
  devices: DeviceService;
  events: EventService;
  experienceService: ExperienceService;
  files: FileService;
  imageRegistryService: ImageRegistryService;
  healthService: HealthService;
  integrationService: IntegrationService;
  layoutService: LayoutService;
  limitSummary: LimitService;
  meteringService: MeteringService;
  notifications: NotificationService;
  orgService: OrgService;
  queryService: QueryService;
  topics: TopicService;
  tokens: TokenService;
  triggerService: TriggerService;
  users: UserService;
  workspaceService: WorkspaceService;
  secretService: SecretService;
}

export const defaultDomainContextFactory = (overrides?: Partial<Context>) => ({
  get actionService(): ActionService {
    throw new Error("ActionService is missing in DomainServicesContext");
  },
  get adminService(): AdminService {
    throw new Error("AdminService is missing in DomainServicesContext");
  },
  get configService(): ConfigService {
    throw new Error("ConfigService is missing in DomainServicesContext");
  },
  get datasetService(): DatasetService {
    throw new Error("DatasetService is missing in DomainServicesContext");
  },
  get devices(): DeviceService {
    throw new Error("DeviceService is missing in DomainServicesContext");
  },
  get events(): EventService {
    throw new Error("EventService is missing in DomainServicesContext");
  },
  get experienceService(): ExperienceService {
    throw new Error("ExperienceService is missing in DomainServicesContext");
  },
  get files(): FileService {
    throw new Error("FileService is missing in DomainServicesContext");
  },
  get healthService(): HealthService {
    throw new Error("HealthService is missing in DomainServicesContext");
  },
  get integrationService(): IntegrationService {
    throw new Error("IntegrationService is missing in DomainServicesContext");
  },
  get layoutService(): LayoutService {
    throw new Error("LayoutService is missing in DomainServicesContext");
  },
  get imageRegistryService(): ImageRegistryService {
    throw new Error("ImageRegistryService is missing in DomainServicesContext");
  },
  get limitSummary(): LimitService {
    throw new Error("LimitService is missing in DomainServicesContext");
  },
  get meteringService(): MeteringService {
    throw new Error("MeteringService is missing in DomainServicesContext");
  },
  get queryService(): QueryService {
    throw new Error("QueryService is missing in DomainServicesContext");
  },
  get notifications(): NotificationService {
    throw new Error("NotificationService is missing in DomainServicesContext");
  },
  get orgService(): OrgService {
    throw new Error("OrgService is missing in DomainServicesContext");
  },
  get topics(): TopicService {
    throw new Error("TopicService is missing in DomainServicesContext");
  },
  get tokens(): TokenService {
    throw new Error("TokenService is missing in DomainServicesContext");
  },
  get triggerService(): TriggerService {
    throw new Error("TriggerService is missing in DomainServicesContext");
  },
  get users(): UserService {
    throw new Error("UserService is missing in DomainServicesContext");
  },
  get workspaceService(): WorkspaceService {
    throw new Error("WorkspaceService is missing in DomainServicesContext");
  },
  get secretService(): SecretService {
    throw new Error("SecretService is missing in DomainServicesContext");
  },
  ...overrides,
});

export const DomainServicesContext = React.createContext<Context | null>(null);

export const useDomainServices = (): Context => {
  const domainServices = React.useContext(DomainServicesContext);
  if (!domainServices) {
    throw new Error("DomainServices is null", {
      cause: {
        reason: "DomainServices is accessed outside a DomainServicesProvider",
      },
    });
  }
  return domainServices;
};
